/**
 * ProjectionChart.tsx — the hand-rolled SVG projection curve + kg↔st toggle.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: shown on the projection interstitial AND the eligible result screen. The
 * curve is drawn from {@link projectionSeries} (10 monthly points easing to the
 * goal) plus a flat, dashed maintenance tail — no chart library, just SVG
 * (bundle-lean). Colours are CSS variables, so it re-skins for dark mode. The
 * disclaimer renders UNCONDITIONALLY inside this component so no screen can show
 * the curve without it (spec-mandatory "illustrative, not a guarantee"). Copy is
 * category-level only (R28).
 *
 * On mount the curve plays a one-off "loading reveal": the line draws itself
 * (SVG stroke-dashoffset, CSS-transitioned) with the shaded area and goal label
 * fading in behind it, while a dot travels the curve from the start point to
 * the goal point (requestAnimationFrame, eased). `usePrefersReducedMotion` gates
 * all of it — reduced-motion users (and any environment without `matchMedia`,
 * e.g. non-browser test runners) get the finished chart on the very first
 * paint, no animation. The exported hook is reused by
 * {@link ../journey/ProjectionInterstitial} to gate its headline counter on the
 * same signal.
 */
import { useEffect, useState } from 'react';
import { KG_UNIT_SEPARATOR } from '../lib/format';
import { useRevealAfterMount, usePrefersReducedMotion } from '../ui/motion';
import {
  PROJECTION_DISCLAIMER,
  PROJECTION_MONTHS,
  goalWeightKg,
  projectedLossKg,
  projectionSeries,
} from './projection';
import { kgToSt, round1 } from './units';

// The reduced-motion hook's canonical home moved to ui/motion (it is now shared
// by the FadeIn/Skeleton/BmiDial polish too); re-exported here so this module's
// existing consumers (ProjectionInterstitial) keep importing it unchanged.
export { usePrefersReducedMotion } from '../ui/motion';

/** Chart geometry (viewBox units — scales responsively via width 100%). */
const W = 460;
const H = 190;
const X0 = 30;
const X_GOAL = W - 96;
const X_END = W - 20;
const Y0 = 22;
const Y1 = H - 34;

/** Duration of the mount "loading reveal" (line draw + travelling dot). */
const REVEAL_MS = 1300;

/**
 * Format a kg value in the active display unit, with a NO-BREAK space before
 * the unit (`KG_UNIT_SEPARATOR`, lib/format.ts) so a figure never wraps away
 * from it: on a phone "about 21 kg lost." used to break as "about 21" /
 * "kg lost." (review, 2026-09-25 — the claim that every journey weight keeps
 * its unit was true of `formatKg` and not of this chart). Exported for its spec.
 */
export function formatWeight(kg: number, unit: 'kg' | 'st'): string {
  return unit === 'st' ? `${kgToSt(kg)}${KG_UNIT_SEPARATOR}st` : `${round1(kg)}${KG_UNIT_SEPARATOR}kg`;
}

function toggleStyle(active: boolean) {
  return {
    // ≥44px tap target with roomy padding for phones (was 36px — E3 #10).
    padding: '10px 20px',
    minHeight: '44px',
    borderRadius: 'var(--gps-r-pill)',
    border: `1.5px solid ${active ? 'var(--gps-primary)' : 'var(--gps-border-strong)'}`,
    background: active ? 'var(--gps-primary-light)' : 'var(--gps-surface)',
    color: 'var(--gps-text)',
    font: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  } as const;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * 0→1 progress for the travelling dot, advanced via requestAnimationFrame and
 * eased out. Jumps straight to 1 (final position, no animation) when
 * `disabled` or rAF isn't available. Restarts whenever `resetKey` changes and
 * always tears its frame down on unmount.
 */
function useRevealProgress(disabled: boolean, resetKey: unknown): number {
  const [progress, setProgress] = useState(() => (disabled ? 1 : 0));

  useEffect(() => {
    if (disabled || typeof requestAnimationFrame !== 'function') {
      setProgress(1);
      return;
    }
    setProgress(0);
    let frameId = 0;
    let start: number | null = null;
    const tick = (now: number) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / REVEAL_MS);
      setProgress(easeOutCubic(t));
      if (t < 1) frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
    // resetKey (the weight) intentionally restarts the reveal for a new value.
  }, [disabled, resetKey]);

  return progress;
}

/** The projection curve for a starting weight, with unit toggle + disclaimer. */
export function ProjectionChart({ weightKg }: { weightKg: number }) {
  const [unit, setUnit] = useState<'kg' | 'st'>('kg');
  const reduced = usePrefersReducedMotion();
  const progress = useRevealProgress(reduced, weightKg);
  // The line/area/label reveal is flipped a frame after mount so the CSS
  // transition on stroke-dashoffset/opacity actually plays; `weightKg` restarts
  // it for a new value (shared with the rest of the polish via ui/motion).
  const revealed = useRevealAfterMount(reduced, weightKg);

  if (!Number.isFinite(weightKg) || weightKg <= 0) {
    return (
      <div style={{ padding: 'var(--gps-s-4)', textAlign: 'center' }}>
        <p style={{ fontSize: '13px', color: 'var(--gps-muted)', margin: 0 }}>Weight projection unavailable</p>
      </div>
    );
  }

  const series = projectionSeries(weightKg);
  const goal = goalWeightKg(weightKg);
  const loss = projectedLossKg(weightKg);

  const span = weightKg - goal || 1;
  const points = series.map((w, month) => {
    const x = X0 + ((X_GOAL - X0) * month) / PROJECTION_MONTHS;
    const y = Y0 + ((Y1 - Y0) * (weightKg - w)) / span;
    return [x, y] as const;
  });
  const line = `M${points.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ')}`;
  const area = `${line} L ${X_GOAL} ${Y1} L ${X0} ${Y1} Z`;
  const [startX, startY] = points[0];
  const [goalX, goalY] = points[points.length - 1];

  // Cumulative arc length of the polyline, so the travelling dot can be placed
  // at a constant-speed point along the ACTUAL drawn curve for any `progress`.
  let lineLength = 0;
  const cumLens = [0];
  for (let i = 1; i < points.length; i++) {
    const [x0, y0] = points[i - 1];
    const [x1, y1] = points[i];
    lineLength += Math.hypot(x1 - x0, y1 - y0);
    cumLens.push(lineLength);
  }
  const dotTarget = progress * lineLength;
  let [dotX, dotY] = points[0];
  for (let i = 1; i < points.length; i++) {
    if (dotTarget <= cumLens[i] || i === points.length - 1) {
      const segLen = cumLens[i] - cumLens[i - 1];
      const segT = segLen === 0 ? 1 : (dotTarget - cumLens[i - 1]) / segLen;
      const [x0, y0] = points[i - 1];
      const [x1, y1] = points[i];
      dotX = x0 + (x1 - x0) * segT;
      dotY = y0 + (y1 - y0) * segT;
      break;
    }
  }

  const fadeTransition = reduced ? 'none' : `opacity ${REVEAL_MS}ms ease-out`;

  return (
    <figure style={{ margin: 0 }}>
      <div
        role="group"
        aria-label="Chart weight units"
        style={{ display: 'flex', gap: '8px', marginBottom: 'var(--gps-s-2)' }}
      >
        <button type="button" style={toggleStyle(unit === 'kg')} onClick={() => setUnit('kg')}>
          kg
        </button>
        <button type="button" style={toggleStyle(unit === 'st')} onClick={() => setUnit('st')}>
          st
        </button>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', display: 'block' }}
        role="img"
        aria-label={`Illustrative weight projection from ${formatWeight(weightKg, unit)} to ${formatWeight(goal, unit)} over ${PROJECTION_MONTHS} months, then maintenance`}
      >
        <line x1={X0} y1={Y1} x2={X_END} y2={Y1} style={{ stroke: 'var(--gps-border)' }} strokeWidth="1" />
        <path
          d={area}
          style={{ fill: 'var(--gps-primary-light)', opacity: revealed ? 0.7 : 0, transition: fadeTransition }}
        />
        <path
          d={line}
          fill="none"
          style={{
            stroke: 'var(--gps-primary)',
            strokeDasharray: lineLength,
            strokeDashoffset: revealed ? 0 : lineLength,
            transition: reduced ? 'none' : `stroke-dashoffset ${REVEAL_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`,
          }}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Flat maintenance tail after the goal month (dashed). */}
        <path
          d={`M ${X_GOAL} ${Y1} L ${X_END} ${Y1}`}
          fill="none"
          style={{
            stroke: 'var(--gps-primary)',
            opacity: revealed ? 1 : 0,
            transition: fadeTransition,
          }}
          strokeWidth="2.4"
          strokeDasharray="2 5"
          strokeLinecap="round"
        />
        <circle cx={startX} cy={startY} r="4.5" style={{ fill: 'var(--gps-primary-dark)' }} />
        {/* Travelling dot: sits on the start point at progress 0, settles on the goal point at progress 1. */}
        <circle cx={dotX} cy={dotY} r="5.5" style={{ fill: 'var(--gps-primary)' }} />
        <circle cx={dotX} cy={dotY} r="2" fill="#fff" />
        <text x={startX + 6} y={startY - 8} fontSize="12" fontWeight="600" style={{ fill: 'var(--gps-text)' }}>
          {formatWeight(weightKg, unit)}
        </text>
        {/* Goal-weight label sits ABOVE the goal dot so it never collides with the "N months" axis label below. */}
        <text
          x={goalX}
          y={goalY - 10}
          fontSize="12"
          fontWeight="600"
          style={{ fill: 'var(--gps-text)', opacity: revealed ? 1 : 0, transition: fadeTransition }}
          textAnchor="middle"
        >
          {formatWeight(goal, unit)}
        </text>
        <text x={X0} y={H - 8} fontSize="11" style={{ fill: 'var(--gps-muted)' }}>
          Today
        </text>
        <text x={X_GOAL} y={H - 8} fontSize="11" style={{ fill: 'var(--gps-muted)' }} textAnchor="middle">
          {PROJECTION_MONTHS} months
        </text>
      </svg>
      <p style={{ fontSize: '1.05rem', color: 'var(--gps-text)', margin: 'var(--gps-s-2) 0 0' }}>
        Around <b>{formatWeight(goal, unit)}</b> in {PROJECTION_MONTHS} months — about{' '}
        <b>{formatWeight(loss, unit)}</b> lost.
      </p>
      <figcaption
        style={{
          fontSize: '11.5px',
          color: 'var(--gps-muted)',
          background: 'var(--gps-primary-light)',
          borderRadius: 'var(--gps-r-sm)',
          padding: '9px 11px',
          marginTop: 'var(--gps-s-2)',
        }}
      >
        {PROJECTION_DISCLAIMER}
      </figcaption>
    </figure>
  );
}
